import { useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from './Cards/Card';
import InverterCard from './Cards/InverterCard';
import { ComponentId } from '../../../models';
import { Site } from '../../../models/site';
import PVIcon from 'src/assets/icons/pv-icon.png';
import GeneratorIcon from 'src/assets/icons/generator-icon.png';
import BatteryIcon from 'src/assets/icons/battery-icon.png';
import EVIcon from 'src/assets/icons/ev-charging-icon.png';
import GridIcon from 'src/assets/icons/grid-icon.png';
import houseIcon from 'src/assets/icons/loads-icon.png';
import { useEquipment } from './useEquipment';
import { usePowerFlowCenter } from './usePowerFlowCenter';
import { ComponentFlow, AdditionalMetricsValue } from './types';
import { useMediaQuery } from '@material-ui/core';
import { FORTRESS_GUARDIAN } from 'src/data/constants';

const MAX_WIDTH = '712px';

const useStyles = makeStyles({
  powerFlowContainer: {
    display: 'grid',
    padding: '48px 0',
    width: '100%',
    position: 'relative',
    margin: '0 auto',
    maxWidth: '1280px',
  },
  topRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 0.6fr 0.6fr 1fr',
    gridTemplateAreas: '"r1-first r1-second r1-third r1-fourth"',
    gap: 16,
    alignItems: 'start',
    [`@media (max-width: ${MAX_WIDTH})`]: {
      gridTemplateRows: 'auto auto',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateAreas: '"r1-first r1-fourth" "r1-second r1-third"',
      gap: 30,
    },
  },
  middleRow: {
    display: 'grid',
    margin: '152px 0',
    justifyContent: 'center',
    [`@media (max-width: ${MAX_WIDTH})`]: {
      margin: '40px 0',
    },
  },
  bottomRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridTemplateAreas: '"r3-first r3-second r3-third"',
    gap: 48,
    alignItems: 'end',
    '@media (max-width: 712px)': {
      gridTemplateRows: 'auto auto',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateAreas: '"r3-second empty" "r3-first r3-third"',
      gap: 30,
    },
  },

  loadsPosition: {
    justifySelf: 'center',
    [`@media (max-width: ${MAX_WIDTH})`]: {
      justifySelf: 'start',
    },
  },
  justifyEnd: {
    justifySelf: 'end',
  },
  justifyStart: {
    [`@media (max-width: ${MAX_WIDTH})`]: {
      justifySelf: 'left',
    },
  },
  justifyCenter: {
    justifySelf: 'center',
  },
});

interface PowerFlowDiagramProps {
  flows: ComponentFlow[];
  site: Site;
}

const batteryHalfPath = (batteryFlow: number, pvFlow: number) => {
  if (pvFlow > 0 && batteryFlow > 0) {
    return false;
  } else if (pvFlow > 0 && batteryFlow < 0) {
    return true;
  } else if (pvFlow === 0 && Math.abs(batteryFlow) > 0) {
    return false;
  } else {
    return false;
  }
};

const evChargerHalfPath = (gridFlow: number) => gridFlow !== 0;

const getFooterText = (
  value: AdditionalMetricsValue,
  unit: AdditionalMetricsValue
): string | null => {
  if (typeof value === 'boolean') return null;
  return value ? `${value} ${unit}` : null;
};

const getUtilityStatus = (status: AdditionalMetricsValue): boolean | undefined => {
  if (status === undefined || typeof status !== 'boolean') return undefined;
  return status;
};

const PowerFlowDiagram: React.FC<PowerFlowDiagramProps> = ({ flows, site }) => {
  const isSmallScreen = useMediaQuery(`(max-width: ${MAX_WIDTH})`);
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const inverterRef = useRef<HTMLDivElement>(null);
  const isResidentialSite = site.dataPartnerId === FORTRESS_GUARDIAN;

  const { powerFlowCenter } = usePowerFlowCenter(containerRef, inverterRef);

  const flowObj = useMemo(() => {
    return flows.reduce((acc, flow) => {
      acc[flow.id] = {
        ...flow,
        flow: Number(flow?.flow?.toFixed(1)),
      };
      return acc;
    }, {} as Record<ComponentId, ComponentFlow>);
  }, [flows]);
  const siteEquipment = useEquipment(site); // move to the loader

  return (
    <div className={classes.powerFlowContainer} ref={containerRef}>
      <div className={classes.topRow}>
        <Card
          siteId={site.id}
          componentId={ComponentId.Solar}
          icon={PVIcon}
          title="PV"
          badge={'DC'}
          gridArea="r1-first"
          installed={flowObj[ComponentId.Solar].installed}
          linkTagsIds={flowObj[ComponentId.Solar]?.linkTagsId}
          equipments={siteEquipment[ComponentId.Solar]}
          powerFlow={{
            endX: powerFlowCenter.x,
            endY: powerFlowCenter.y,
            flow: flowObj[ComponentId.Solar]?.flow,
            invertFlow: flowObj[ComponentId.Solar]?.flow < 0,
            active: flowObj[ComponentId.Solar]?.active,
            cornerNearStart: isSmallScreen,
          }}
          status={{
            value: Math.abs(flowObj[ComponentId.Solar]?.flow || 0),
            valueUnit: flowObj[ComponentId.Solar]?.unit,
          }}
        />
        <Card
          siteId={site.id}
          componentId={ComponentId.Generator}
          icon={GeneratorIcon}
          title="Generator"
          badge="AC"
          gridArea="r1-second"
          installed={flowObj[ComponentId.Generator].installed}
          linkTagsIds={flowObj[ComponentId.Generator]?.linkTagsId}
          powerFlow={{
            endX: powerFlowCenter.x,
            endY: powerFlowCenter.y,
            flow: flowObj[ComponentId.Generator]?.flow,
            active: flowObj[ComponentId.Generator]?.active,
          }}
          status={{
            value: Math.abs(flowObj[ComponentId.Generator]?.flow || 0),
            valueUnit: flowObj[ComponentId.Generator]?.unit,
          }}
        />
        <Card
          siteId={site.id}
          componentId={ComponentId.PvInverter}
          className={classes.justifyEnd}
          icon={PVIcon}
          title="Pv inverter"
          badge="AC"
          gridArea="r1-third"
          installed={flowObj[ComponentId.PvInverter].installed}
          linkTagsIds={flowObj[ComponentId.PvInverter]?.linkTagsId}
          powerFlow={{
            endX: powerFlowCenter.x,
            endY: powerFlowCenter.y,
            flow: flowObj[ComponentId.PvInverter]?.flow,
            active: flowObj[ComponentId.PvInverter]?.active,
          }}
          status={{
            value: Math.abs(flowObj[ComponentId.PvInverter]?.flow || 0),
            valueUnit: flowObj[ComponentId.PvInverter]?.unit,
          }}
        />
        {isSmallScreen ? (
          <Card
            siteId={site.id}
            componentId={ComponentId.Storage}
            className={classes.justifyEnd}
            icon={BatteryIcon}
            title="Battery"
            gridArea="r1-fourth"
            badge="DC"
            installed={flowObj[ComponentId.Storage].flow !== undefined}
            linkTagsIds={flowObj[ComponentId.Storage]?.linkTagsId}
            footerText={
              [
                getFooterText(
                  flowObj[ComponentId.Storage]?.additionalMetrics?.storage_v,
                  `${flowObj[ComponentId.Storage]?.additionalMetrics?.storage_v_unit}dc`
                ),
              ].filter(Boolean) as string[]
            }
            powerFlow={{
              endX: powerFlowCenter.x,
              endY: powerFlowCenter.y,
              flow: flowObj[ComponentId.Storage]?.flow,
              active: flowObj[ComponentId.Storage]?.active,
              cornerNearStart: isSmallScreen,
              halfPath: batteryHalfPath(
                flowObj[ComponentId.Storage]?.flow,
                flowObj[ComponentId.Solar]?.flow
              ),
            }}
            status={{
              percentage: Math.abs(
                flowObj[ComponentId.Storage]?.additionalMetrics?.state_of_charge as number
              ),
              value: Math.abs(flowObj[ComponentId.Storage]?.flow || 0),
              valueUnit: flowObj[ComponentId.Storage]?.unit,
            }}
            equipments={siteEquipment[ComponentId.Storage]}
          />
        ) : (
          <Card
            siteId={site.id}
            componentId={ComponentId.Utility}
            className={classes.justifyEnd}
            icon={GridIcon}
            title="Grid"
            gridArea="r1-fourth"
            badge="AC"
            installed={flowObj[ComponentId.Utility].flow !== undefined}
            linkTagsIds={flowObj[ComponentId.Utility]?.linkTagsId}
            footerText={
              [
                getFooterText(
                  flowObj[ComponentId.Utility]?.additionalMetrics?.utility_hz,
                  flowObj[ComponentId.Utility]?.additionalMetrics?.utility_hz_unit as string
                ),
                getFooterText(
                  flowObj[ComponentId.Utility]?.additionalMetrics?.utility_vac,
                  flowObj[ComponentId.Utility]?.additionalMetrics?.utility_vac_unit as string
                ),
              ].filter(Boolean) as string[]
            }
            powerFlow={{
              endX: powerFlowCenter.x,
              endY: powerFlowCenter.y,
              flow: flowObj[ComponentId.Utility]?.flow,
              active: flowObj[ComponentId.Utility]?.active,
              cornerNearStart: isSmallScreen,
            }}
            status={{
              value: Math.abs(flowObj[ComponentId.Utility]?.flow || 0),
              valueUnit: flowObj[ComponentId.Utility]?.unit,
              onOfGrid: getUtilityStatus(
                flowObj[ComponentId.Utility]?.additionalMetrics?.utility_status
              ),
            }}
          />
        )}
      </div>

      <div className={classes.middleRow}>
        <InverterCard
          siteId={site.id}
          componentId={ComponentId.Inverter}
          ref={inverterRef}
          zIndex={10}
          equipments={siteEquipment[ComponentId.Inverter]}
          linkTagsIds={flowObj[ComponentId.Inverter]?.linkTagsId}
          isResidentialSite={isResidentialSite}
        />
      </div>

      <div className={classes.bottomRow}>
        {isSmallScreen ? (
          <Card
            siteId={site.id}
            componentId={ComponentId.Utility}
            className={classes.justifyStart}
            icon={GridIcon}
            title="Grid"
            gridArea="r3-first"
            badge="AC"
            installed={flowObj[ComponentId.Utility].installed}
            linkTagsIds={flowObj[ComponentId.Utility]?.linkTagsId}
            footerText={
              [
                getFooterText(
                  flowObj[ComponentId.Utility]?.additionalMetrics?.utility_hz,
                  flowObj[ComponentId.Utility]?.additionalMetrics?.utility_hz_unit
                ),
                getFooterText(
                  flowObj[ComponentId.Utility]?.additionalMetrics?.utility_vac,
                  flowObj[ComponentId.Utility]?.additionalMetrics?.utility_vac_unit
                ),
              ].filter(Boolean) as string[]
            }
            powerFlow={{
              endX: powerFlowCenter.x,
              endY: powerFlowCenter.y,
              flow: flowObj[ComponentId.Utility]?.flow,
              active: flowObj[ComponentId.Utility]?.active,
              cornerNearStart: isSmallScreen,
            }}
            status={{
              value: Math.abs(flowObj[ComponentId.Utility]?.flow || 0),
              valueUnit: flowObj[ComponentId.Utility]?.unit,
            }}
          />
        ) : (
          <Card
            siteId={site.id}
            componentId={ComponentId.Storage}
            icon={BatteryIcon}
            className={classes.justifyStart}
            title="Battery"
            gridArea="r3-first"
            badge="DC"
            installed={flowObj[ComponentId.Storage].installed}
            linkTagsIds={flowObj[ComponentId.Storage]?.linkTagsId}
            footerText={
              [
                getFooterText(
                  flowObj[ComponentId.Storage]?.additionalMetrics?.storage_v,
                  `${flowObj[ComponentId.Storage]?.additionalMetrics?.storage_v_unit}dc`
                ),
              ].filter(Boolean) as string[]
            }
            powerFlow={{
              endX: powerFlowCenter.x,
              endY: powerFlowCenter.y,
              flow: flowObj[ComponentId.Storage]?.flow,
              active: flowObj[ComponentId.Storage]?.active,
              cornerNearStart: isSmallScreen,
              halfPath: batteryHalfPath(
                flowObj[ComponentId.Storage]?.flow,
                flowObj[ComponentId.Solar]?.flow
              ),
            }}
            status={{
              percentage: Math.abs(
                flowObj[ComponentId.Storage]?.additionalMetrics?.state_of_charge as number
              ),
              value: Math.abs(flowObj[ComponentId.Storage]?.flow || 0),
              valueUnit: flowObj[ComponentId.Storage]?.unit,
            }}
            equipments={siteEquipment[ComponentId.Storage]}
          />
        )}
        <Card
          siteId={site.id}
          componentId={ComponentId.Load}
          icon={houseIcon}
          title="Loads"
          gridArea="r3-second"
          badge="AC"
          installed={flowObj[ComponentId.Load].installed}
          className={classes.loadsPosition}
          linkTagsIds={flowObj[ComponentId.Load]?.linkTagsId}
          powerFlow={{
            endX: powerFlowCenter.x,
            endY: powerFlowCenter.y,
            flow: flowObj[ComponentId.Load]?.flow * -1,
            active: flowObj[ComponentId.Load]?.active,
          }}
          status={{
            value: Math.abs(flowObj[ComponentId.Load]?.flow || 0),
            valueUnit: flowObj[ComponentId.Load]?.unit,
          }}
        />
        <Card
          siteId={site.id}
          componentId={ComponentId.EVCharger}
          icon={EVIcon}
          title="EV charger"
          gridArea="r3-third"
          badge="AC"
          installed={flowObj[ComponentId.EVCharger].installed}
          className={classes.justifyEnd}
          linkTagsIds={flowObj[ComponentId.EVCharger]?.linkTagsId}
          powerFlow={{
            endX: powerFlowCenter.x,
            endY: powerFlowCenter.y,
            flow: flowObj[ComponentId.EVCharger]?.flow * -1,
            active: flowObj[ComponentId.EVCharger]?.active,
            cornerNearStart: isSmallScreen,
            halfPath: evChargerHalfPath(flowObj[ComponentId.Utility]?.flow),
          }}
          status={{
            value: Math.abs(flowObj[ComponentId.EVCharger]?.flow || 0),
            valueUnit: flowObj[ComponentId.EVCharger]?.unit,
          }}
        />
      </div>
    </div>
  );
};

export default PowerFlowDiagram;
